import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Loader2, Package, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API, fmt } from "../constants/site";
import Logo from "../components/Logo";

export default function ConfirmOrderPage() {
  const [params] = useSearchParams();
  const orderNumber = (params.get("order") || "").trim();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderNumber) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    fetch(`${API}/orders/${encodeURIComponent(orderNumber)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error();
        setOrder(await r.json());
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  const act = async (action) => {
    if (action === "cancelled" && !window.confirm("Are you sure you want to cancel this order?")) return;
    setBusy(action);
    setError("");
    try {
      const res = await fetch(`${API}/orders/${encodeURIComponent(orderNumber)}/customer-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not update your order");
      setOrder(data);
      toast.success(action === "confirmed" ? "Order confirmed — thank you!" : "Your order has been cancelled");
    } catch (e) {
      setError(e.message || "Could not update your order");
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-oat-100" data-testid="confirm-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sage-600" />
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-oat-100 px-5" data-testid="confirm-not-found">
        <div className="w-full max-w-md rounded-3xl border border-charcoal-900/10 bg-white p-10 text-center">
          <Package className="mx-auto h-10 w-10 text-sage-600" strokeWidth={1.4} />
          <h1 className="mt-4 font-serif text-2xl text-charcoal-900">Order link not found</h1>
          <p className="mt-2 text-sm text-charcoal-500">
            This confirmation link looks incomplete. Please use the exact link we sent you on WhatsApp, or contact us for help.
          </p>
          <Link to="/" className="mt-6 inline-block text-sm font-medium text-sage-600 hover:underline">
            ← Visit OmniMist Kitchen Craft
          </Link>
        </div>
      </div>
    );
  }

  const confirmed = order.customer_confirmation === "confirmed";
  const cancelled = order.status === "cancelled" || order.customer_confirmation === "cancelled";
  const onWay = ["shipped", "delivered", "returned"].includes(order.status);

  return (
    <div className="min-h-screen bg-oat-100 px-5 py-10" data-testid="confirm-page">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center">
          <Link to="/" aria-label="OmniMist home" className="inline-block">
            <Logo />
          </Link>
        </div>

        {confirmed ? (
          <div className="mt-6 rounded-3xl border border-charcoal-900/10 bg-white p-8 text-center" data-testid="confirm-success-view">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage-100">
              <Check className="h-8 w-8 text-sage-600" strokeWidth={2.4} />
            </span>
            <h1 className="mt-5 font-serif text-3xl text-charcoal-900">Order confirmed!</h1>
            <p className="mt-3 text-sm leading-relaxed text-charcoal-500">
              Thank you, {order.customer.full_name.split(" ")[0]}! Your OmniMist is being packed and will ship within 24 hours. Updates will arrive on WhatsApp and email.
            </p>
            <div className="mt-6 rounded-2xl bg-oat-100 p-5 text-left">
              <div className="flex justify-between text-sm text-charcoal-500">
                <span>Order</span>
                <span className="font-semibold text-charcoal-900" data-testid="confirm-order-number">{order.order_number}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-charcoal-500">
                <span>Total (COD)</span>
                <span className="font-semibold text-charcoal-900">{fmt(order.total)}</span>
              </div>
            </div>
          </div>
        ) : cancelled ? (
          <div className="mt-6 rounded-3xl border border-charcoal-900/10 bg-white p-8 text-center" data-testid="cancel-success-view">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fdecec]">
              <X className="h-8 w-8 text-[#b91c1c]" strokeWidth={2.4} />
            </span>
            <h1 className="mt-5 font-serif text-3xl text-charcoal-900">Order cancelled</h1>
            <p className="mt-3 text-sm leading-relaxed text-charcoal-500">
              Your order {order.order_number} has been cancelled. Since this was Cash on Delivery, nothing was charged. Changed your mind? Just order again anytime.
            </p>
          </div>
        ) : (
          <div className="mt-6 rounded-3xl border border-charcoal-900/10 bg-white p-8" data-testid="confirm-card">
            <h1 className="text-center font-serif text-3xl text-charcoal-900">
              {onWay ? "Your order is on its way" : "Please confirm your order"}
            </h1>
            <p className="mt-2 text-center text-sm text-charcoal-500">
              {onWay
                ? "This order has already been handed to the courier — no action needed."
                : "One tap so we know the order is genuine and can start packing."}
            </p>

            <div className="mt-6 rounded-2xl bg-oat-100 p-5" data-testid="confirm-order-summary">
              <div className="flex justify-between text-sm text-charcoal-500">
                <span>Order</span>
                <span className="font-semibold text-charcoal-900" data-testid="confirm-order-number">{order.order_number}</span>
              </div>
              {order.items.map((it, i) => (
                <div key={i} className="mt-2 flex justify-between text-sm text-charcoal-500" data-testid="confirm-order-items">
                  <span>{it.quantity}× OmniMist™ 2-in-1 Sprayer</span>
                  <span className="text-charcoal-900">{fmt(it.line_total)}</span>
                </div>
              ))}
              <div className="mt-3 flex justify-between border-t border-charcoal-900/10 pt-3 text-sm">
                <span className="text-charcoal-500">Total on delivery</span>
                <span className="font-serif text-lg font-semibold text-charcoal-900" data-testid="confirm-order-total">{fmt(order.total)}</span>
              </div>
              <div className="mt-3 flex justify-between text-sm text-charcoal-500">
                <span>Delivering to</span>
                <span className="font-medium text-charcoal-900">
                  {order.customer.full_name}, {order.customer.city}
                </span>
              </div>
            </div>

            {!onWay && (
              <>
                <div className="mt-6 grid grid-cols-1 gap-3">
                  <button
                    onClick={() => act("confirmed")}
                    disabled={!!busy}
                    data-testid="confirm-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-sage-500 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-sage-600 disabled:opacity-60"
                  >
                    {busy === "confirmed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Confirm My Order
                  </button>
                  <button
                    onClick={() => act("cancelled")}
                    disabled={!!busy}
                    data-testid="cancel-order-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#b91c1c]/40 bg-white py-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#b91c1c] transition-colors hover:bg-[#fdecec] disabled:opacity-60"
                  >
                    {busy === "cancelled" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                    Cancel My Order
                  </button>
                </div>
                {error && (
                  <p className="mt-3 text-center text-xs font-medium text-[#b91c1c]" data-testid="confirm-error">
                    {error}
                  </p>
                )}
              </>
            )}

            <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-charcoal-500">
              <ShieldCheck className="h-3.5 w-3.5 text-sage-600" /> Order {order.order_number} · OmniMist™ Kitchen Craft
            </p>
          </div>
        )}

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-charcoal-500">
          <MessageCircle className="h-3.5 w-3.5" /> Questions? Just reply to our WhatsApp message.
        </p>
      </div>
    </div>
  );
}
